import { ColumnFiltersState, SortingState } from '@tanstack/react-table'

import { compareValueWithPrefix } from '@/helpers/compareValueWithPrefix'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'
import { isAfter } from 'date-fns'
import { UpdatedLog } from '@/types/logs'

const getFieldValue = (row: UpdatedLog, columnId: string): any => {
    if (columnId === COMBINED_LOGS_COLUMNS.Time) {
        return row.timestamp
    }

    return (row as any)[columnId]
}

const extractParts = (value: string): [string, string, string] => {
    const match = value.match(/^([A-Za-z]*)(\d+)([A-Za-z]*)$/)

    if (!match) {
        return ['', '', '']
    }

    return [match[1], match[2], match[3]]
}

export const filterAndSortUpdatedRecords = (
    data: UpdatedLog[],
    sortingState: SortingState,
    filteringState: ColumnFiltersState
): { filteredIds: string[]; sortedIds: string[] } => {
    const groupMap = new Map<string, UpdatedLog[]>()

    for (const item of data) {
        const groupId = item.groupId || 'ungrouped'
        if (!groupMap.has(groupId)) {
            groupMap.set(groupId, [])
        }
        groupMap.get(groupId)!.push(item)
    }

    const passesFilter = (item: UpdatedLog): boolean => {
        return filteringState.every(({ id, value }) => {
            const fieldValue = getFieldValue(item, id)

            if (Array.isArray(value)) {
                return value.some((val) => {
                    if (Array.isArray(fieldValue)) {
                        return fieldValue.includes(val)
                    }
                    return String(fieldValue).toLowerCase().includes(String(val).toLowerCase())
                })
            }

            if (typeof value === 'string') {
                const fieldFormattedValue = String(fieldValue).toLowerCase()
                const formattedValue = value.toLowerCase()

                return id === COMBINED_LOGS_COLUMNS.Code
                    ? fieldFormattedValue === formattedValue
                    : fieldFormattedValue.includes(formattedValue)
            }

            return true
        })
    }

    const filteredGroups = Array.from(groupMap.values()).filter((group) => group.some(passesFilter))

    const getRepresentativeValue = (group: UpdatedLog[], columnId: string) => {
        // prefer an item in the group that has a non-empty value for the column
        for (const item of group) {
            const val = getFieldValue(item, columnId)
            if (val != null && !(Array.isArray(val) && val.length === 0) && String(val) !== '') {
                return val
            }
        }

        // fallback to first element
        return getFieldValue(group[0], columnId)
    }

    const sortedGroups = filteredGroups.sort((groupA, groupB) => {
        for (const sort of sortingState) {
            const aVal = getRepresentativeValue(groupA, sort.id)
            const bVal = getRepresentativeValue(groupB, sort.id)

            if (aVal == null) {
                return 1
            }
            if (bVal == null) {
                return -1
            }

            // handle time column separately (compare as dates)
            if (sort.id === COMBINED_LOGS_COLUMNS.Timestamp) {
                const aDate = aVal as unknown as string
                const bDate = bVal as unknown as string

                if (isAfter(new Date(bDate), new Date(aDate))) return 1
                if (isAfter(new Date(aDate), new Date(bDate))) return -1
                continue
            }

            if (Array.isArray(aVal) || Array.isArray(bVal)) {
                if (aVal.length !== bVal.length) {
                    return aVal.length - bVal.length
                }

                return aVal.join(',') > bVal.join(',') ? 1 : -1
            }

            const [prefixA, numA, suffixA] = extractParts(aVal)
            const [prefixB, numB, suffixB] = extractParts(bVal)

            const comparison = compareValueWithPrefix([prefixA, numA, suffixA], [prefixB, numB, suffixB])
            if (comparison !== 0) return comparison
        }

        return 0
    })

    return {
        filteredIds: [...new Set(filteredGroups.flat().map((item) => item.id))],
        sortedIds: [...new Set(sortedGroups.flat().map((item) => item.id))],
    }
}
